import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import {
    DayPicker,
    DayPickerProps,
    DateRange,
    DayPickerRangeProps,
    DayPickerBase,
} from 'react-day-picker-for-new-calendar';
import dayPickerStyles from 'react-day-picker-for-new-calendar/dist/style.module.css';
import { ru } from 'date-fns/locale';
import cn from 'clsx';
import { Divider } from '@mui/material';

import { CalendarTypes } from 'Modules/ui3/types';

import { Box } from '../Box';
import { Stack } from '../Stack';
import { Container } from './Container';
import { Day } from './Day';
import { CaptionLabel } from './CaptionLabel';
import { Months } from './Months';
import { Ranges } from './Ranges';
import { ButtonsContainer } from './ButtonsContainer';
import styles from './index.module.scss';
import { CaptionSelector } from './CaptionSelector';
import { ValueDateList } from './ValueDateList';
import { MonthYearData } from './types';
import { Button2 } from '../Button2';
import { TimePicker } from './TimePicker';


type LocaleProps = Partial<Pick<DayPickerProps, 'locale'>>;

export type BaseCalendarProps2<TValue = any> = {
    value: TValue;
    onChange: (value: TValue) => void;
    locale?: 'ru';
    buttons?: ReactNode;
    initialFocus?: DayPickerBase['initialFocus'];
};

export type CalendarProps2 = BaseCalendarProps2<Date> & {
    calendarType?: CalendarTypes;
};


const LOCALIZATION_PROPS: Record<BaseCalendarProps2['locale'], LocaleProps> = {
    ru: {
        locale: ru,
    },
};


function useBaseProps<TProps extends DayPickerBase>(
    locale: BaseCalendarProps2['locale'] = 'ru',
): TProps {
    return {
        ...LOCALIZATION_PROPS[locale],
        components: {
            Day,
            Months,
            CaptionLabel,
        },
        classNames: {
            ...dayPickerStyles,
            root: cn(styles.container, dayPickerStyles.root),
            months: cn(styles.months, dayPickerStyles.months),
            row: cn(styles.week, dayPickerStyles.row),
            day: cn(styles.day, dayPickerStyles.day),
            nav: cn(styles.nav, dayPickerStyles.nav),
            cell: cn(styles.cell, dayPickerStyles.cell),
            head_cell: cn(styles.weekDay, dayPickerStyles.head_cell),
            caption: null,
            day_today: cn(styles.today, dayPickerStyles.day_today),
            day_range_start: styles.rangeStart,
            day_range_end: styles.rangeEnd,
            day_range_middle: styles.rangeMiddle,
            day_selected: cn(styles.selected, dayPickerStyles.day_selected),
        },
    } as TProps;
}


const DayPickerMemoized = React.memo(DayPicker);

export const Calendar2: FC<CalendarProps2> = React.memo(({
    value,
    onChange,
    locale = 'ru',
    calendarType,
}) => {
    const [{ isOpen, currentDate, monthSelected, yearSelected }, setMonthYearData] =
    useState<MonthYearData>({
        isOpen: false,
        currentDate: new Date(),
        monthSelected: false,
        yearSelected: false,
    });

    const [selectedDate, setSelectedDate] = useState<Date>();

    useEffect(() => {
        if (!value)
            setSelectedDate(undefined);
    }, [setSelectedDate, value]);

    useEffect(() => {
        if (value) {
            setMonthYearData(({ currentDate, ...rest }) => ({
                ...rest,
                currentDate: new Date(currentDate.setFullYear(
                    value.getFullYear(),
                    value.getMonth(),
                )),
            }));
        }
    }, [setMonthYearData, value]);

    useEffect(() => {
        if (monthSelected && yearSelected) {
            setMonthYearData(({ isOpen, ...rest }) => ({
                ...rest,
                isOpen: !isOpen,
            }));
        }
    }, [monthSelected, yearSelected, setMonthYearData]);

    const selectedDateHandler = useCallback<(choosenValue: Date) => void>(choosenValue =>
        setSelectedDate(() => {
            if (!value)
                return choosenValue;

            const newDate = new Date(choosenValue);

            newDate.setHours(value.getHours());
            newDate.setMinutes(value.getMinutes());

            return newDate;
        }), [setSelectedDate, value]);

    if (isOpen) {
        return (
            <Container
                sx={{
                    paddingBottom: 0,
                    width: ({ spacing }) => spacing(70),
                }}
            >
                <CaptionSelector
                    displayMonth={currentDate}
                    onClick={() => {
                        setMonthYearData(({ isOpen, ...rest }) => ({
                            ...rest,
                            isOpen: !isOpen,
                        }));
                    }}
                    sx={{
                        backgroundColor: 'grayscale.shade10',
                    }}
                />

                <Stack
                    mt={11}
                    flexDirection='row'
                >
                    <ValueDateList
                        type='months'
                        setSelectedDate={setMonthYearData}
                    />

                    <ValueDateList
                        type='years'
                        setSelectedDate={setMonthYearData}
                    />
                </Stack>
            </Container>
        );
    }

    return (
        <Container>
            <Stack
                direction='row'
            >
                <Stack
                    direction='row'
                    width={({ spacing }) => spacing(66)}
                >
                    <DayPickerMemoized
                        {...LOCALIZATION_PROPS[locale]}
                        mode='single'

                        selected={selectedDate || value}
                        onDayClick={calendarType === 'withTimePicker' ? selectedDateHandler : onChange}

                        month={currentDate}
                        onMonthChange={month => setMonthYearData(({ currentDate, ...rest }) => ({
                            ...rest,
                            currentDate: new Date(currentDate.setFullYear(
                                month.getFullYear(),
                                month.getMonth(),
                            )),
                        }))}

                        components={{
                            Day,
                            CaptionLabel: () => (
                                <CaptionLabel
                                    displayMonth={currentDate}
                                    onClick={() => {
                                        setMonthYearData(({ isOpen, ...rest }) => ({
                                            ...rest,
                                            isOpen: !isOpen,
                                        }));
                                    }}
                                />
                            ),
                        }}

                        classNames={{
                            root: cn(styles.root, dayPickerStyles.root),
                            nav: cn(styles.navigation, dayPickerStyles.nav),
                            table: cn(styles.tableDays, dayPickerStyles.table),
                            head: cn(styles.headWeeks, dayPickerStyles.head),
                            head_cell: cn(styles.headCellWeeks, dayPickerStyles.head_cell),
                        }}
                    />
                </Stack>

                {calendarType !== 'onlyDate' && (
                    <>
                        <Divider
                            orientation='vertical'
                            flexItem
                            sx={{
                                mx: 4,
                                borderColor: 'grayscale.shade20',
                            }}
                        />

                        <Box
                            width={({ spacing }) => spacing(18)}
                            height={({ spacing }) => spacing(62)}
                            overflow='hidden'
                        >
                            <TimePicker
                                selectedDate={selectedDate || value}
                                timeSetter={setSelectedDate}
                            />
                        </Box>
                    </>
                )}
            </Stack>

            {calendarType !== 'onlyDate' && (
                <ButtonsContainer>
                    <Button2
                        variantType='brand'
                        variant='primary'
                        size='small'
                        fullWidth
                        onClick={() => onChange(selectedDate ?? value)}
                    >
                        Подтвердить
                    </Button2>

                    <Button2
                        variant='stroke'
                        size='small'
                        fullWidth
                        onClick={() => {
                            onChange(null);
                            setSelectedDate(undefined);
                        }}
                    >
                        Сбросить
                    </Button2>
                </ButtonsContainer>
            )}
        </Container>
    );
});


export type RangeCalendarProps2 = BaseCalendarProps2<DateRange>;


// старая реализация диапазона выбора даты - будет правиться по мере необходимости
export const RangeCalendar2: FC<RangeCalendarProps2> = ({
    value,
    onChange,
    locale = 'ru',
    buttons,
    initialFocus,
}) => {
    const baseProps = useBaseProps<DayPickerRangeProps>(locale);

    return (
        <Container>
            <DayPicker
                mode='range'
                selected={value}
                onSelect={useCallback(range => onChange(range), [onChange])}
                numberOfMonths={2}
                modifiers={{
                    isRange: true,
                }}
                modifiersClassNames={{
                    isRange: styles.isRange,
                }}
                initialFocus={initialFocus}
                {...baseProps}
            />
            <Ranges locale={locale} value={value} onChange={onChange} />
            {buttons && <ButtonsContainer>{buttons}</ButtonsContainer>}
        </Container>
    );
};
